import { useMemo, useCallback, useState, useEffect } from "react";
import {
  PencilIcon,
  ClockIcon,
  TrashIcon,
  ChartBarIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import IsolatedHTML from "../ui/IsolatedHTML";
import { Dialog, DialogPanel, DialogBackdrop } from "@headlessui/react";
import { ACTION_TYPE_META } from "../workflow/WorkflowCards";
import { NODE_TYPE } from "../../services/Constants";
import { Toggle } from "../form-fields/Toggle";
import { APIs } from "../../services/Apis";
import { toast } from "react-toastify";
import { ActionAnalyticsPanel } from "./ActionAnalyticsPanel";
import AddActionDialog from "../dialogs/AddActionDialog";

const CARD_BASE =
  "w-full h-auto bg-white transition flex flex-col shrink-0 overflow-hidden";

export const SELECTED_TYPE = Object.freeze({
  GOAL: "goal",
  ACTION: "action",
});

function formatDelay(delay, delayUnit = "days") {
  const unitMap = {
    minutes: delay === 1 ? "minute" : "minutes",
    hours: delay === 1 ? "hour" : "hours",
    days: delay === 1 ? "day" : "days",
  };
  const unitLabel =
    unitMap[delayUnit] || (delay === 1 ? delayUnit.slice(0, -1) : delayUnit);
  return delay === 0 ? "Immediately" : `Wait ${delay} ${unitLabel}`;
}

function getNodeLabel(action, isGoal, isDelay) {
  if (isGoal) return "Goal";
  if (isDelay) return "Delay";
  const meta = ACTION_TYPE_META[action?.type];
  if (meta) return meta.label;
  if (action?.type === "email") return "Email";
  return action?.type || "Action";
}

function findTriggerActions(segment, segments) {
  if (!segment || !segments?.length) return [];

  if (segment.actions?.length > 0) return segment.actions;
  if (segment.segmentActions?.length > 0) return segment.segmentActions;

  for (const stakeholder of segments) {
    for (const trigger of stakeholder.triggers || []) {
      if (String(trigger.id) === String(segment.id || segment.segmentId)) {
        return trigger.actions || [];
      }
    }
  }
  return [];
}

function PendingApprovalBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border border-amber-200 bg-amber-50 text-amber-700">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="h-3 w-3"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
      Pending Approval
    </span>
  );
}

function EmailShimmer() {
  return (
    <div className="animate-pulse flex flex-col gap-3">
      <div className="relative bg-slate-100 border border-slate-200 rounded-lg p-3 flex flex-col gap-2.5">
        <div className="h-3 w-full bg-slate-200 rounded" />
        <div className="h-3 w-5/6 bg-slate-200 rounded" />
        <div className="h-3 w-4/6 bg-slate-200 rounded" />
        <div className="h-20 w-full bg-slate-200 rounded mt-1" />
        <div className="h-3 w-3/4 bg-slate-200 rounded" />
        <div className="h-3 w-2/3 bg-slate-200 rounded" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-medium text-slate-400 animate-none">
            Generating email...
          </span>
        </div>
      </div>
    </div>
  );
}

function GoalShimmer() {
  return (
    <div className="animate-pulse flex flex-col gap-2.5">
      <div className="h-3 w-full bg-slate-200 rounded" />
      <div className="h-3 w-4/5 bg-slate-200 rounded" />
      <div className="h-3 w-3/5 bg-slate-200 rounded" />
      <div className="flex items-center gap-2 mt-1">
        <div className="h-3 w-3 bg-slate-200 rounded-full animate-spin border-2 border-slate-300 border-t-blue-400" />
        <span className="text-xs font-medium text-slate-400">Generating goal...</span>
      </div>
    </div>
  );
}

const WorkflowCard = ({
  isGoal,
  action,
  segmentTitle,
  isSelected,
  onDelete,
  onEdit,
  onAnalytics,
  labelOverride,
  delayLabel,
  onDelayClick,
  toggleActive,
  toggleEnabling,
  onToggle,
  notReady,
}) => {
  const label = labelOverride || getNodeLabel(action, isGoal, false);
  const isEmail =
    !isGoal &&
    (action?.type === NODE_TYPE.EMAIL || action?.emailContent !== undefined);

  const selectedBorderClass = isGoal
    ? "border-2 border-yellow-400 bg-yellow-50/50 shadow-md ring-4 ring-yellow-400/20"
    : "border-2 border-blue-400 bg-blue-50/50 shadow-md ring-4 ring-blue-400/20";

  const defaultBorderClass = isGoal
    ? "border-slate-200"
    : "border-slate-200";

  return (
    <div
      className={`${CARD_BASE} ${isSelected ? selectedBorderClass : defaultBorderClass}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-slate-900 truncate">
            {label}
          </span>
          {action?.approved === false && <PendingApprovalBadge />}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isGoal && onToggle && action?.approved !== false && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-2"
            >
              <span className="text-xs text-slate-500">
                {toggleActive ? "Active" : "Inactive"}
              </span>
              <Toggle
                checked={toggleActive}
                onChange={onToggle}
                disabled={toggleEnabling}
              />
            </div>
          )}
          {delayLabel && (
            delayLabel === "Immediately" ? (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200">
                <ClockIcon className="h-3 w-3" />
                <span>{delayLabel}</span>
              </span>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onDelayClick) onDelayClick();
                }}
                className="group/delay flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors cursor-pointer"
              >
                <ClockIcon className="h-3 w-3" />
                <span>{delayLabel}</span>
                <PencilIcon className="h-3 w-3 opacity-0 group-hover/delay:opacity-100 transition-opacity" />
              </button>
            )
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-100" />

      {/* Body */}
      <div className="px-4 py-3">
        {isGoal && (
          notReady ? (
            <GoalShimmer />
          ) : (
            <p className="text-[13px] text-slate-600 leading-relaxed line-clamp-3 whitespace-normal">
              {segmentTitle || action?.description || "Untitled Goal"}
            </p>
          )
        )}

        {isEmail && (
          <div className="flex flex-col">
            {action?.emailSubject && (
              <p className="mt-1 text-sm font-semibold text-slate-900 whitespace-normal break-words mb-3">
                Subject: {action.emailSubject}
              </p>
            )}
            {action?.ready === false ? (
              <EmailShimmer />
            ) : (
              (action?.htmlEmailContent || action?.emailContent) && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 pointer-events-none">
                  <IsolatedHTML
                    html={action.htmlEmailContent || action.emailContent}
                    style={{ zoom: "0.5", width: "100%" }}
                  />
                </div>
              )
            )}
          </div>
        )}

        {!isGoal && !isEmail && (
          <div>
            {action?.title && (
              <p className="text-[13px] font-medium text-slate-700 line-clamp-2 whitespace-normal break-words">
                {action.title}
              </p>
            )}
            {action?.emailSubject && (
              <p className="mt-1 text-xs text-slate-500 line-clamp-1 whitespace-normal break-words">
                {action.emailSubject}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-100 px-4 py-2.5 flex items-center gap-2">
        {isGoal && (
          <>
            {onEdit && !notReady && (
              <button
                onClick={onEdit}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors cursor-pointer"
              >
                <PencilIcon className="h-3.5 w-3.5" />
                Edit Goal
              </button>
            )}
            {onAnalytics && !notReady && (
              <button
                onClick={onAnalytics}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors cursor-pointer"
              >
                <ChartBarIcon className="h-3.5 w-3.5" />
                Analytics
              </button>
            )}
          </>
        )}
        {!isGoal && (
          <>
            {onEdit && (
              <button
                onClick={onEdit}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors cursor-pointer"
              >
                <PencilIcon className="h-3.5 w-3.5" />
                Edit
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm("Are you sure you want to delete this action?")) {
                    onDelete(action?.id || action?.segmentActionId);
                  }
                }}
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors cursor-pointer"
              >
                <TrashIcon className="h-3.5 w-3.5" />
                Delete
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const GoalWorkflowPanel = ({
  isOpen,
  segment,
  segments = [],
  onClose,
  selectedType,
  selectedAction,
  onSelectGoal,
  onSelectAction,
  onRefresh,
  onActionOverride,
  isOnboarding = false,
  loading = false,
  inline = false,
}) => {
  const triggerActions = useMemo(
    () => findTriggerActions(segment, segments),
    [segment, segments],
  );

  const allActions =
    triggerActions.length > 0
      ? triggerActions
      : segment?.actions || segment?.segmentActions || [];

  const [enabling, setEnabling] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showAddAction, setShowAddAction] = useState(false);

  const anyNotReady = useMemo(() => {
    if (!segment) return false;
    if (segment.ready === false) return true;
    return allActions.some((action) => action.ready === false);
  }, [segment, allActions]);

  const isLoading = loading || anyNotReady;

  const allApproved = useMemo(() => {
    if (!segment) return false;
    if (!segment.approved) return false;
    if (allActions.length === 0) return false;
    return allActions.every((action) => action.approved);
  }, [segment, allActions]);

  const [localActive, setLocalActive] = useState(segment?.active ?? false);

  useEffect(() => {
    setLocalActive(segment?.active ?? false);
  }, [segment?.active]);

  const handleToggleEnable = async (newActive) => {
    if (!segment) return;
    const targetId = segment.id || segment.segmentId;

    if (newActive && !allApproved) {
      toast.error(
        "All actions and the trigger must be approved before enabling this workflow.",
      );
      return;
    }

    setEnabling(true);
    try {
      const response = await APIs.enableSegment(targetId, newActive);
      if (response?.success) {
        setLocalActive(newActive);
        toast.success(
          newActive
            ? "Workflow enabled successfully!"
            : "Workflow disabled successfully!",
        );
        if (onRefresh) onRefresh();
      } else {
        toast.error(response?.message || "Failed to update workflow status");
      }
    } catch (error) {
      console.error("Error toggling workflow:", error);
    } finally {
      setEnabling(false);
    }
  };

  const handleDeleteAction = useCallback(
    async (actionId) => {
      if (!actionId) return;
      try {
        const response = await APIs.deleteSegmentAction(actionId);
        if (response?.success) {
          toast.success("Action deleted successfully!");
          if (onRefresh) onRefresh();
        } else {
          toast.error(response?.message || "Failed to delete action");
        }
      } catch (error) {
        console.error("Error deleting action:", error);
        toast.error("Failed to delete action");
      }
    },
    [onRefresh],
  );

  const selectedId = selectedAction?.segmentActionId || selectedAction?.id;

  const handleCardClick = useCallback(
    (action, isGoal) => {
      if (isGoal) {
        onSelectGoal();
      } else if (action) {
        onSelectAction(action);
      }
    },
    [onSelectGoal, onSelectAction],
  );

  const flowContent = (
    <div
      className={`relative flex flex-col h-full w-full bg-white overflow-hidden ${inline ? "min-h-[500px]" : ""}`}
    >
      {/* Scrollable card list */}
      <div className="flex-1 overflow-y-auto bg-[#fafafa] pb-[100px] min-h-[100vh]">
        <div className="flex flex-col items-center gap-2 px-4 pt-4">
          {/* Goal card */}
          <WorkflowCard
            isGoal
            action={segment}
            segmentTitle={segment?.description || segment?.title}
            isSelected={selectedType === SELECTED_TYPE.GOAL}
            onEdit={() => handleCardClick(segment, true)}
            onAnalytics={() => !isOnboarding && setShowAnalytics(true)}
            toggleActive={localActive}
            toggleEnabling={enabling}
            onToggle={!isOnboarding ? handleToggleEnable : undefined}
            notReady={segment?.ready === false}
          />

          {/* Action cards (delays merged into preceding action's header) */}
          {(() => {
            const items = [];
            let pendingDelay = null;
            let isFirstAction = true;
            let emailCount = 0;

            for (const action of allActions) {
              const isDelay =
                action.type === NODE_TYPE.DELAY || action.type === "delay";

              if (isDelay) {
                pendingDelay = action;
              } else {
                const isEmail =
                  action.type === NODE_TYPE.EMAIL ||
                  action.type === "email" ||
                  action.emailContent !== undefined;
                let labelOverride = null;
                if (isEmail) {
                  emailCount++;
                  if (emailCount > 1) {
                    labelOverride = `Follow Up Email ${emailCount - 1}`;
                  }
                }

                const actionId = action.id || action.segmentActionId;
                const isSelected =
                  selectedType === SELECTED_TYPE.ACTION &&
                  (selectedId === actionId ||
                    selectedId === action.segmentActionId);

                const delayLabel = pendingDelay
                  ? formatDelay(pendingDelay.delay, pendingDelay.delayUnit)
                  : isFirstAction
                    ? "Immediately"
                    : null;

                const delayAction = pendingDelay;

                items.push(
                  <WorkflowCard
                    key={actionId}
                    action={action}
                    isSelected={isSelected}
                    onDelete={handleDeleteAction}
                    onEdit={() => handleCardClick(action, false)}
                    labelOverride={labelOverride}
                    delayLabel={delayLabel}
                    onDelayClick={
                      delayAction
                        ? () => onSelectAction(delayAction)
                        : undefined
                    }
                  />,
                );

                pendingDelay = null;
                isFirstAction = false;
              }
            }

            return items;
          })()}

          {/* Add action button */}
          <button
            onClick={() => {
              if (isOnboarding && onActionOverride) return onActionOverride();
              setShowAddAction(true);
            }}
            className="flex items-center justify-center w-10 h-10 rounded-full border-2 border-dashed border-slate-300 text-slate-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition-colors cursor-pointer mt-2 mb-8"
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <AddActionDialog
        open={showAddAction}
        setOpen={setShowAddAction}
        segmentId={segment?.id || segment?.segmentId}
        onSuccess={onRefresh}
      />
    </div>
  );

  if (inline) {
    if (!isOpen) return null;
    return (
      <>
        {flowContent}
        <ActionAnalyticsPanel
          isOpen={showAnalytics}
          onClose={() => setShowAnalytics(false)}
          segmentId={segment?.id || segment?.segmentId}
        />
      </>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} className="relative z-40">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm transition-opacity duration-300 ease-in-out data-closed:opacity-0"
        />
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <DialogPanel
                transition
                className="pointer-events-auto w-screen max-w-2xl transform transition duration-300 ease-in-out data-closed:translate-x-full flex flex-col bg-white shadow-2xl overflow-hidden"
              >
                {flowContent}
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
      <ActionAnalyticsPanel
        isOpen={showAnalytics}
        onClose={() => setShowAnalytics(false)}
        segmentId={segment?.id || segment?.segmentId}
      />
    </>
  );
};
